/**
 * AccountPicker — searchable dropdown for selecting one of N Google Ads accounts.
 * Renders a button showing the current selection. Click to open a panel with
 * a search input + scrollable list. Designed to handle 100+ accounts gracefully.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

const AccountPicker = ({ accounts, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.customer_id || '').includes(q),
    );
  }, [accounts, query]);

  return (
    <div className="relative" ref={ref} data-testid="account-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-text-primary bg-white border border-border-subtle rounded-md hover:border-brand transition-colors min-w-[260px] justify-between"
        data-testid="account-picker-trigger"
      >
        <span className="truncate text-left">
          {selected ? (
            <>
              {selected.name}
              <span className="text-text-tertiary text-xs ml-1.5">({selected.customer_id})</span>
            </>
          ) : (
            <span className="text-text-tertiary">Select account…</span>
          )}
        </span>
        <ChevronDown size={14} className={`text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 w-[420px] max-w-[90vw] bg-white border border-border-subtle rounded-lg shadow-lg overflow-hidden"
          data-testid="account-picker-panel"
        >
          <div className="relative border-b border-border-subtle">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              autoFocus
              type="search"
              placeholder={`Search ${accounts.length} accounts…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm focus:outline-none"
              data-testid="account-picker-search"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-text-muted py-6 text-center">No matches</p>
            ) : (
              filtered.map((a) => {
                const isSelected = a.id === selected?.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onSelect(a);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-page transition-colors ${
                      isSelected ? 'bg-brand/5' : ''
                    }`}
                    data-testid={`account-picker-option-${a.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-primary truncate flex items-center gap-1.5">
                        {a.name}
                        {a.is_demo && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                            Demo
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-text-tertiary truncate">{a.customer_id}</p>
                    </div>
                    {isSelected && <Check size={14} className="text-brand flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
          {filtered.length > 0 && filtered.length !== accounts.length && (
            <div className="px-3 py-1.5 text-[11px] text-text-tertiary border-t border-border-subtle bg-surface-page">
              {filtered.length} of {accounts.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountPicker;
